import { useTranslation } from '@kostbar/i18n'
import type { StockCorrectionRequired } from '@/api/endpoints/product-stock'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { formatStockQuantity } from '@/shared/lib/stock-quantity'

interface StockCorrectionDialogProps {
  /** The 409 body; the dialog is open while it is set. */
  details: StockCorrectionRequired | null
  productName: string
  /** The product's base unit as shown ("Stk" by default). */
  unit: string
  isPending: boolean
  /** The details were refreshed by a refused confirmation: the stock changed meanwhile. */
  refreshed?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Confirms the correction to 0 that a save books when a product becomes a dish or its tracking is
 * turned off (CHG-2026-09-stock-usage-and-price-twins, D10, task 10.4). Driven entirely by the
 * `409 stock_correction_required` body: it lists every restaurant whose stock is not 0.
 */
export function StockCorrectionDialog({
  details,
  productName,
  unit,
  isPending,
  refreshed = false,
  onConfirm,
  onCancel,
}: StockCorrectionDialogProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const isDish = details?.reason === 'converted_to_dish'

  return (
    <AlertDialog
      open={details !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onCancel()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isDish
              ? tProducts('stockCorrection.dishTitle')
              : tProducts('stockCorrection.trackingOffTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isDish
              ? tProducts('stockCorrection.dishDescription', {
                  name: productName,
                })
              : tProducts('stockCorrection.trackingOffDescription', {
                  name: productName,
                })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {refreshed && (
          <p role="status" className="text-sm font-medium text-warning">
            {tProducts('stockCorrection.refreshed')}
          </p>
        )}

        <div className="rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm space-y-1">
          <p className="font-medium">
            {tProducts('stockCorrection.listTitle')}
          </p>
          <ul className="space-y-1">
            {(details?.corrections ?? []).map((correction) => (
              <li
                key={correction.restaurantId}
                className="flex justify-between gap-4"
              >
                <span>
                  {correction.restaurantName ?? String(correction.restaurantId)}
                </span>
                <span className="tabular-nums">
                  {tProducts('stockCorrection.toZero', {
                    qty: formatStockQuantity(correction.availableQty),
                    unit,
                  })}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">
            {isDish
              ? tProducts('stockCorrection.dishNote')
              : tProducts('stockCorrection.trackingOffNote')}
          </p>
        </div>

        {isDish && (
          <p className="text-sm text-muted-foreground">
            {tProducts('stockCorrection.dishReservations')}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {tCommon('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(e) => {
              // Keep the dialog open until the save answers; success closes it.
              e.preventDefault()
              onConfirm()
            }}
          >
            {tProducts('stockCorrection.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
